import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import * as XLSX from 'xlsx';
import { getAdminTenantRouteScopeId } from "../../utils/auth";

/* --------------------------------- Tenant Listing ------------------------------ */

export interface Tenant {
  tenantId: number;
  tenantName: string;
  principalId: number;
  programId: number;
  propertyId: number | null;
  wing: string;
  floor: string;
  tenantBrand: string;
  remark: string | null;
  storeImage: string | null;
  storeLogo: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  latitude: number | null;
  longitude: number | null;
  registrationDocument: string | null;
  storeCoordinator: string | null;
  email: string | null;
  phone: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  propertyName?: string;
  propertyAddress?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyPincode?: string;
  vpaCount?: number;
  vpaConsentText: string | null;
}

export interface TenantListResponse {
  total_count: number;
  total_pages: number;
  tenants: Tenant[];
  page_size: number;
  page: number;
}

export const fetchTenantList = createAsyncThunk("tenant/fetchTenantList", async ({ principalId = null, programId = null, limit = 10, page = 1, searchTerm = "" }: { principalId?: number | null | string; programId?: number | null | string; limit?: number; page?: number; searchTerm?: string }, { rejectWithValue }) => {
  const tenantScopeId = getAdminTenantRouteScopeId();
  const token = localStorage.getItem("accessToken");
  try {
    const params: any = { limit, page };
    if (principalId) params["principalId"] = principalId;
    if (programId) params["programId"] = programId;
    if (searchTerm) params["tenantName"] = searchTerm;

    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/programs/tenant/${tenantScopeId}`,
      timeout: 10000,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      method: "GET",
      params: params,
    });

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Failed to fetch tenants");
  }
});

/* --------------------------------- Update Tenant ------------------------------ */

export const updateTenant = createAsyncThunk("tenant/updateTenant", async ({ tenantId, formData }: { tenantId: number; formData: FormData }, { rejectWithValue }) => {
  const token = localStorage.getItem("accessToken");
  const tenantScopeId = getAdminTenantRouteScopeId();
  try {
    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/programs/tenant/${tenantScopeId}/${tenantId}`,
      timeout: 10000,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      method: "PUT",
      data: formData,
    });

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Failed to update tenant");
  }
});

/* --------------------------------- Delete Tenant ------------------------------ */

export const deleteTenant = createAsyncThunk(
  "tenant/deleteTenant",
  async (tenantId: number, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken");
    const tenantScopeId = getAdminTenantRouteScopeId();
   
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/tenant/${tenantScopeId}/${tenantId}`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "DELETE",
      });

      return { tenantId, data: response.data };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || "Failed to delete tenant");
    }
  }
);

/* --------------------------------- Tenant Vpa Mapping ------------------------------ */

export interface VpaDetails {
  vpaId: number;
  vpa: string;
  providerName: string;
  merchantNameFromVpa: string | null;
  qrImage: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  hasUtr: boolean;
  utr: null | string;
  hasConsent: boolean;
  vpaConsent: null | string;
}

export interface TenantVpaMapping {
  tenantId: number;
  vpaId: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  vpa: VpaDetails[];
}

export const fetchTenantVpaMappings = createAsyncThunk("tenant/fetchTenantVpaMappings", async (tenantId: number, { rejectWithValue }) => {
  const token = localStorage.getItem("accessToken");
  try {
    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/programs/mappings?tenantId=${tenantId}`,
      timeout: 10000,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      method: "GET",
    });

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Failed to fetch tenant VPA mappings");
  }
});

/* --------------------------------- Bulk Tenant Upload ------------------------------ */

interface BulkTenantResponse {
  tenantName: string;
  principalId: string;
  programId: string;
  property: string;
  wing: string;
  floor: string;
  tenantBrand: string;
  remark: string;
  email: string;
  storeCoordinator: string;
  phone: string
}

interface BulkTenantPayload {
  'Outlet Name': string;
  OutletBrand: string;
  status: string;
  reason: string;
  remark: string;
  ContactEmail:string;
  ContactPerson: string;
  ContactMobile: string;
  wing: string;
  floor: string;
  property: string;
  Mall: string
 
}


export interface TenantUploadResult {
  tenantId: number | null;
  operation: string;
  userId: number;
  statusCode: "failed" | "success"; // If status codes have fixed values
  message: string;
  time: number;
  response: BulkTenantResponse | null;
  payload: BulkTenantPayload
}

export interface BulkTenantUploadResponse {
  tenants: TenantUploadResult[] | null;
}

export const uploadTenantBulk = createAsyncThunk("tenant/bulkUpload", async ({ xlsxFile, principalId, programId }: { xlsxFile: File; principalId: string; programId: string }, { rejectWithValue }) => {
  const formData = new FormData();

  const updateXlsxWithPrincipalAndProgramId = async (file: File, principalId: string, programId: string): Promise<File> => {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    
    // Get headers from the first row
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    const headers: string[] = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: C })];
      headers.push(cell?.v || '');
    }

    // Add principalId and programId columns at the end if not already present
    if (!headers.includes('principalId')) headers.push('principalId');
    if (!headers.includes('programId')) headers.push('programId');

    // Convert to JSON with updated headers
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    
    // Filter out empty rows and update data
    const updatedData = jsonData
      .filter((row: any) => row.some((cell: any) => cell !== undefined && cell !== '')) // Remove empty rows
      .map((row: any, index) => {
        if (index === 0) {
          return headers;
        }

        // Create array matching headers length
        const newRow = new Array(headers.length).fill('');
        
        // Copy existing data
        row.forEach((cell: any, idx: number) => {
          newRow[idx] = cell;
        });

        // Set principalId and programId at correct indices
        const principalIdIndex = headers.indexOf('principalId');
        const programIdIndex = headers.indexOf('programId');
        if (principalIdIndex !== -1) newRow[principalIdIndex] = principalId;
        if (programIdIndex !== -1) newRow[programIdIndex] = programId;

        // Convert array to object with headers
        const rowObject: any = {};
        headers.forEach((header, idx) => {
          rowObject[header] = newRow[idx];
        });
        return rowObject;
      });

    // Convert back to worksheet maintaining column order
    const updatedWorksheet = XLSX.utils.json_to_sheet(updatedData, { header: headers });
    const updatedWorkbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(updatedWorkbook, updatedWorksheet, 'Sheet1');

    // Generate new file
    const xlsxOutput = XLSX.write(updatedWorkbook, { bookType: 'xlsx', type: 'array' });
    return new File([xlsxOutput], file.name, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };

  const updatedXlsxFile = await updateXlsxWithPrincipalAndProgramId(xlsxFile, principalId, programId);
  formData.append("file", updatedXlsxFile);

  const token = localStorage.getItem("accessToken");

  try {
    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/programs/uploadTenant/sheet`,
      
      timeout: 10000,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      data: formData,
      method: "POST",
    });
    if (response.data?.operations && response.data.operations.length === 1 && response.data.operations[0]?.message === "Error processing file") {
      return rejectWithValue(response.data.operations[0].message);
    }

    return {
      tenants: response.data?.operations as TenantUploadResult,
    };
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message ? error?.response?.data?.message : "Failed to Upload Tenants");
  }
});

/* --------------------------------- Update VPA Status ------------------------------ */

export const updateVpaStatus = createAsyncThunk("tenant/updateVpaStatus", async ({ vpaId, status }: { vpaId: number; status: string }, { rejectWithValue }) => {
  const token = localStorage.getItem("accessToken");
  try {
    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/programs/vpas/${vpaId}`,
      timeout: 10000,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      method: "PUT",
      data: { status },
    });

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Failed to update VPA status");
  }
});

/* --------------------------------- Program VPA Mapping ------------------------------ */

export interface ProgramVpaMapping {
  tenantId: number;
  tenantName: string;
  programId: number;
  principalId: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  vpa: {
    vpaId: number;
    vpa: string;
    providerName: string;
    status: string;
    qrImage: string;
    merchantNameFromVpa: string;
    hasUtr: boolean;
    utr: {
      utr: string;
      transactionDate: string;
    } | null;
    hasConsent: boolean;
    vpaConsent: {
      consentedDate: string;
      name: string;
    } | null;
  }[];
}

export const fetchProgramVpaMappings = createAsyncThunk(
  "tenant/fetchProgramVpaMappings",
  async ({ programId, status = "ACTIVE", limit = 1000, page = 1 }: { programId: number; status?: string; limit?: number; page?: number }, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/mappings/program/${programId}`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "GET",
        params: { status, limit, page },
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || "Failed to fetch program VPA mappings");
    }
  }
);

/* --------------------------------- Main State ------------------------------ */

interface TenantState {
  tenantError: any | null;
  tenantLoading: boolean;
  tenantBulk: BulkTenantUploadResponse | null;
  tenantList: TenantListResponse | null;
  tenantVpaMappings: TenantVpaMapping[] | null;
  programVpaMappings: ProgramVpaMapping[] | null;
  preSelectedPrincipal: string;
  preSelectedProgram: string;
}

const initialState: TenantState = {
  tenantBulk: null,
  tenantError: null,
  tenantLoading: false,
  tenantList: null,
  tenantVpaMappings: null,
  programVpaMappings: null,
  preSelectedPrincipal: "",
  preSelectedProgram: "",
};

const bulkTenantSlice = createSlice({
  name: "tenant",
  initialState,
  reducers: {
    clearBulkTenantUpload(state) {
      state.tenantBulk = null;
      state.tenantLoading = false;
      state.tenantError = null;
    },

    setPreSelectedData(state, action: PayloadAction<{ principalId: string; programId: string }>) {
      state.preSelectedProgram = action.payload.programId;
      state.preSelectedPrincipal = action.payload.principalId;
    },

    clearPreSelectedData(state) {
      state.preSelectedProgram = "";
      state.preSelectedPrincipal = "";
    },

    setTenantLoading(state) {
      state.tenantError = null;
      state.tenantLoading = true;
      state.tenantList = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // Tenant List
      .addCase(fetchTenantList.pending, (state) => {
        state.tenantLoading = true;
        state.tenantError = null;
      })
      .addCase(fetchTenantList.fulfilled, (state, action: PayloadAction<TenantListResponse>) => {
        state.tenantLoading = false;
        state.tenantList = action.payload;
      })
      .addCase(fetchTenantList.rejected, (state, action) => {
        state.tenantLoading = false;
        state.tenantError = action.payload || "Failed to fetch Tenants";
      })

      // Tenant Vpa Mapping List
      .addCase(fetchTenantVpaMappings.pending, (state) => {
        state.tenantLoading = true;
        state.tenantError = null;
      })
      .addCase(fetchTenantVpaMappings.fulfilled, (state, action: PayloadAction<TenantVpaMapping[]>) => {
        state.tenantLoading = false;
        state.tenantVpaMappings = action.payload;
      })
      .addCase(fetchTenantVpaMappings.rejected, (state, action) => {
        state.tenantLoading = false;
        state.tenantError = action.payload || "Failed to fetch Tenants";
      })

      // Update Vpa Status
      .addCase(updateVpaStatus.pending, (state) => {
        state.tenantLoading = true;
        state.tenantError = null;
      })
      .addCase(updateVpaStatus.fulfilled, (state) => {
        state.tenantLoading = false;
      })
      .addCase(updateVpaStatus.rejected, (state, action) => {
        state.tenantLoading = false;
        state.tenantError = action.payload || "Failed to update VPA status";
      })

      // Update Tenant
      .addCase(updateTenant.pending, (state) => {
        state.tenantLoading = true;
        state.tenantError = null;
      })
      .addCase(updateTenant.fulfilled, (state) => {
        state.tenantLoading = false;
      })
      .addCase(updateTenant.rejected, (state, action) => {
        state.tenantLoading = false;
        state.tenantError = action.payload || "Failed to update tenant";
      })

      // Delete Tenant
      .addCase(deleteTenant.pending, (state) => {
        state.tenantLoading = true;
        state.tenantError = null;
      })
      .addCase(deleteTenant.fulfilled, (state, action: PayloadAction<{ tenantId: number; data: any }>) => {
        state.tenantLoading = false;
        if (state.tenantList) {
          state.tenantList.tenants = state.tenantList.tenants.filter(
            (tenant) => tenant.tenantId !== action.payload.tenantId
          );
          state.tenantList.total_count = Math.max(0, state.tenantList.total_count - 1);
        }
      })
      .addCase(deleteTenant.rejected, (state, action) => {
        state.tenantLoading = false;
        state.tenantError = action.payload || "Failed to delete tenant";
      })

      // Program VPA Mappings
      .addCase(fetchProgramVpaMappings.pending, (state) => {
        state.tenantLoading = true;
        state.tenantError = null;
      })
      .addCase(fetchProgramVpaMappings.fulfilled, (state, action: PayloadAction<ProgramVpaMapping[]>) => {
        state.tenantLoading = false;
        state.programVpaMappings = action.payload;
      })
      .addCase(fetchProgramVpaMappings.rejected, (state, action) => {
        state.tenantLoading = false;
        state.tenantError = action.payload || "Failed to fetch program VPA mappings";
      });
  },
});

export const { clearBulkTenantUpload, setPreSelectedData, clearPreSelectedData, setTenantLoading } = bulkTenantSlice.actions;

// Export the reducer
export default bulkTenantSlice.reducer;
